import { useRouteState } from '@shared/utils'
import { observer } from 'mobx-react-lite'
import { ChangeEvent, FC } from 'react'
import { UserDownloadFilesListState } from './state'
import { useDownloadFilesTable } from '@sr/ui'
import { InputSearch, NewDsTableList } from '@shared/ui'
import { ActionsComponent } from './components'

type ListProps = {
  filters?: any
}

export const NewList: FC<ListProps> = observer(({ filters }) => {
  const {
    isLoading,
    downloadFiles,
    onPaginationChange,
    pagination,
    total,
    setSearchInput,
    onSortingChange,
    sourceFilter,
    setSourceFilter,
    hasSearched,
    search,
    searchInput,
    isSearching,
  } = useRouteState(UserDownloadFilesListState, { filters })

  const { columns } = useDownloadFilesTable()

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value)
  }

  const handleSourceChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSourceFilter(e.target.value)
  }

  const sourceOptions = [
    { value: 'all', label: 'Todas las fuentes' },
    { value: 'zoom', label: 'Zoom' },
    { value: 'twilio', label: 'Twilio' },
    { value: 'manual', label: 'Manual' },
  ]

  const getFileType = (format: string): string => {
    const audioFormats = ['mp3', 'wav', 'aac', 'm4a']
    const videoFormats = ['mp4', 'mov', 'avi', 'wmv']

    format = format.toLowerCase()
    if (audioFormats.includes(format)) return 'Audio'
    if (videoFormats.includes(format)) return 'Vídeo'
    return format
  }

  const formatDateTime = (datetime: string) => {
    const date = new Date(datetime);
    const formattedDate = date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).replace(/\//g, '-');
    const formattedTime = date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });

    return `${formattedDate}\n${formattedTime}`;
  }

  const parsePolicyRecordings = (data: any) => {
    if (!data || !Array.isArray(data) || data.length < 1) return []

    return data.map((item: any) => {
      const datetime = formatDateTime(item.datetime);
      const sizeInMB = (parseInt(item.size) / (1024 * 1024)).toFixed(2) + ' MB';

      return {
        name: item.name,
        contractNumber: item.contractNumber,
        datetime: datetime,
        fileName: item.filename,
        format: getFileType(item.format),
        size: sizeInMB,
        download: item.url,
      }
    })
  }

  const dataTable = parsePolicyRecordings(downloadFiles || null)

  return (
    <div className="flex flex-col pb-20 w-[100%]">
      {/* Filtros de búsqueda */}
      <div className="mb-4 space-y-4">
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Buscar por número de póliza
            </label>
            <InputSearch
              onChange={onChange}
            />
          </div>
          <div className="w-64">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fuente de video
            </label>
            <select
              value={sourceFilter}
              onChange={handleSourceChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
            >
              {sourceOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Mensaje de ayuda */}
        {!hasSearched && !search && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <p className="text-sm text-blue-800">
              Ingrese al menos 3 caracteres del número de póliza para buscar los videos asociados.
              Puede filtrar por fuente específica (Zoom, Twilio, Manual) o buscar en todas.
            </p>
          </div>
        )}

        {/* Mensaje de búsqueda en progreso */}
        {isSearching && (
          <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
            <p className="text-sm text-gray-700">
              Buscando videos para "{search}"...
            </p>
          </div>
        )}

        {/* Mensaje cuando el search es muy corto */}
        {searchInput && searchInput.trim().length > 0 && searchInput.trim().length < 3 && !isSearching && (
          <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
            <p className="text-sm text-gray-600">
              Ingrese al menos 3 caracteres para iniciar la búsqueda.
            </p>
          </div>
        )}

        {/* Mensaje cuando no hay resultados */}
        {hasSearched && search && dataTable.length === 0 && !isSearching && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <p className="text-sm text-yellow-800">
              No se encontraron videos para la póliza "{search}" en {sourceFilter === 'all' ? 'ninguna fuente' : `la fuente ${sourceFilter}`}.
            </p>
          </div>
        )}
      </div>

      <NewDsTableList
        data={dataTable || []}
        columns={columns}
        initialPagination={pagination}
        onPaginationChange={onPaginationChange}
        total={total}
        actionsComponent={ActionsComponent}
        isLoading={isLoading}
        onSortingChange={onSortingChange}
        compact
      />
    </div>
  )
})
